import type { DocAsset } from "@/lib/document/assetTypes";
import { DOC_ASSET_TYPE_LABEL } from "./docAssetTypeLabels";

type Props = {
  assets: DocAsset[];
  selectedAssetId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentAssetGrid({ assets, selectedAssetId, onSelect, loading }: Props) {
  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">Loading assets…</p>;
  }

  if (assets.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        No assets in this category yet. Upload an image to build your org library.
      </p>
    );
  }

  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
      {assets.map((asset) => {
        const selected = selectedAssetId === asset.id;
        const isImage = asset.mime_type.startsWith("image/");
        return (
          <li key={asset.id}>
            <button
              type="button"
              onClick={() => onSelect(asset.id)}
              className={[
                "flex h-full w-full flex-col overflow-hidden rounded-lg border text-left transition",
                selected
                  ? "border-indigo-500 ring-2 ring-indigo-200 dark:border-indigo-400 dark:ring-indigo-900/60"
                  : "border-gray-200 hover:border-gray-300 dark:border-zinc-700 dark:hover:border-zinc-600",
              ].join(" ")}
            >
              <div className="flex aspect-[4/3] items-center justify-center bg-gray-50 dark:bg-zinc-900/50">
                {isImage && asset.signed_url ? (
                  <img
                    src={asset.signed_url}
                    alt={asset.display_name}
                    className="max-h-full max-w-full object-contain p-2"
                  />
                ) : (
                  <span className="text-xs text-gray-400 dark:text-zinc-500">No preview</span>
                )}
              </div>
              <div className="space-y-1 border-t border-gray-100 p-2 dark:border-zinc-800">
                <div className="truncate text-sm font-medium text-gray-900 dark:text-zinc-100">
                  {asset.display_name}
                </div>
                <div className="flex flex-wrap items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                  <span>{DOC_ASSET_TYPE_LABEL[asset.asset_type]}</span>
                  <span>·</span>
                  <span>{formatBytes(asset.byte_size)}</span>
                  {asset.width && asset.height ? (
                    <>
                      <span>·</span>
                      <span>
                        {asset.width}×{asset.height}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
